import { useMemo } from 'react';

import type {
  ElectronAPI,
  NativeNotificationPayload,
} from 'src/types/electron';

/**
 * Stable no-op cleanup so subscription callers always receive a callable
 * unsubscribe even when the underlying bridge method is absent.
 */
const noop = (): void => {};

/**
 * Check if the app is running in Electron.
 */
export const isElectron = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    window.electronAPI !== undefined &&
    window.electronAPI.isElectron === true
  );
};

/**
 * Get the Electron API if available.
 * Returns undefined when running in a browser.
 */
export const getElectronAPI = (): ElectronAPI | undefined => {
  if (isElectron()) {
    return window.electronAPI;
  }
  return undefined;
};

/**
 * Hook to check if running in Electron.
 */
export const useIsElectron = (): boolean => {
  return useMemo(() => isElectron(), []);
};

/**
 * The callable surface of the Electron bridge, minus the static presence
 * fields (`platform`, `isElectron`) which callers read via `isElectron()`.
 */
export type SafeElectronAPI = Omit<ElectronAPI, 'platform' | 'isElectron'>;

/**
 * Feature-detecting wrapper over the Electron bridge.
 *
 * Old desktop shells load the live webapp but ship a preload that only exposes
 * a subset of ElectronAPI, so calling a method the installed shell predates
 * (e.g. `onSidebarToggle`) throws and bubbles into PostHogErrorBoundary. Every
 * method here optionally-invokes the underlying bridge (`api?.method?.(...)`)
 * and falls back to a safe default: void methods no-op, subscription methods
 * return the stable `noop` cleanup, and Promise methods resolve empty.
 */
export const getSafeElectronAPI = (): SafeElectronAPI => {
  const api = getElectronAPI();
  return {
    getVersion: () => api?.getVersion?.() ?? Promise.resolve(''),
    showNotification: (payload: NativeNotificationPayload) =>
      api?.showNotification?.(payload),
    onNotificationClicked: (callback) =>
      api?.onNotificationClicked?.(callback) ?? noop,
    setBadgeCount: (count: number) => api?.setBadgeCount?.(count),
    onDeepLinkNavigation: (callback) =>
      api?.onDeepLinkNavigation?.(callback) ?? noop,
    openExternal: (url: string) => api?.openExternal?.(url),
    onSidebarToggle: (callback) => api?.onSidebarToggle?.(callback) ?? noop,
    reportSidebarState: (isCollapsed: boolean) =>
      api?.reportSidebarState?.(isCollapsed),
    reportSidebarFeatureActive: (isActive: boolean) =>
      api?.reportSidebarFeatureActive?.(isActive),
    checkForUpdates: () =>
      api?.checkForUpdates?.() ?? Promise.resolve(undefined),
    onUpdateAvailable: (callback) => api?.onUpdateAvailable?.(callback) ?? noop,
    onUpdateDownloaded: (callback) =>
      api?.onUpdateDownloaded?.(callback) ?? noop,
    installUpdate: () => api?.installUpdate?.(),
  };
};
